import React, { useState, useEffect } from 'react';
import styles from './PremiumModal.module.css';

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PremiumModal: React.FC<PremiumModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Save the original overflow style
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Cleanup function to restore original overflow
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');    try {
      // For PHP backend, use the .php endpoint
      const response = await fetch('/api/waitlist.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('This email is already on our waitlist!');
        }
        throw new Error(data.error || 'Failed to subscribe');
      }

      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      // console.error('❌ Error subscribing to waitlist:', error);
      setError(error instanceof Error ? error.message : 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']} onClick={handleClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        <button 
          className={styles['close-btn']}
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>        {!isSubmitted ? (
          <>
            <div className={styles['modal-header']}>
              <div className={styles['premium-badge']}>PREMIUM</div>
              <h2 className={styles['modal-title']}>Premium Features Coming Soon!</h2>
            </div>

            <div className={styles['waitlist-section']}>
              <div className={styles['form-group']}>
                <label htmlFor="email">Join the waitlist to be notified when Premium launches</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles['email-input']}
                  disabled={isSubmitting}
                />
                {error && <div className={styles['error-message']}>{error}</div>}
              </div>
            </div>

            <div className={styles['scrollable-content']}>
              <div className={styles['features-list']}>
                <div className={styles['feature-item']}>
                  <span className={styles['feature-icon']}></span>
                  <div>
                    <strong>Full HD Downloads</strong>
                    <p>Up to 2048px resolution without watermark</p>
                  </div>
                </div>
                
                <div className={styles['feature-item']}>
                  <span className={styles['feature-icon']}></span>
                  <div>
                    <strong>Exclusive Tattoo Designs</strong>
                    <p>Access to premium tattoo collections</p>
                  </div>
                </div>
                
                <div className={styles['feature-item']}>
                  <span className={styles['feature-icon']}></span>
                  <div>
                    <strong>Advanced Editing Tools</strong>
                    <p>Color filters, effects, and more customization</p>
                  </div>
                </div>
                
                <div className={styles['feature-item']}>
                  <span className={styles['feature-icon']}></span>
                  <div>
                    <strong>And much more...</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['modal-bottom']}>
              <button 
                type="submit" 
                className={styles['submit-btn']}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Subscribing...' : 'Join Waitlist'}
              </button>
              
              <div className={styles['modal-footer']}>
                <p>Be among the first to experience premium features!</p>
              </div>
            </div>
          </>
        ) : (
          <div className={styles['success-message']}>
            <div className={styles['success-icon']}>🎉</div>
            <h2 className={styles['success-title']}>You're on the list!</h2>
            <p>Thank you for joining our waitlist. We'll notify you as soon as Premium features are available.</p>
            <button 
              className={styles['close-success-btn']}
              onClick={handleClose}
            >
              Got it!
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumModal;
